/**
 * Pre-flight validation for AI-generated files.
 *
 * Runs between generation and apply to catch issues early:
 * 1. TypeScript syntax check (via esbuild transform)
 * 2. Import resolution (all relative imports must resolve)
 * 3. Default export check (files imported as default must export default)
 *
 * If a file fails validation, the caller can regenerate just that file.
 */

import * as esbuildWasm from "esbuild-wasm";
import { ensureEsbuild } from "./sandboxBuild";

// ── Types ───────────────────────────────────────────────────────────────────

export interface FileEdit {
  filePath: string;
  action: "create" | "modify" | "delete";
  content?: string;
  explanation: string;
}

export interface ValidationIssue {
  filePath: string;
  type: "syntax" | "import" | "export";
  message: string;
}

export interface PreflightResult {
  passed: boolean;
  issues: ValidationIssue[];
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Resolve a relative import path against the importing file's directory. */
function resolveRelativeImport(fromFile: string, importPath: string): string {
  // Remove leading ./ or ../
  const fromDir = fromFile.includes("/")
    ? fromFile.slice(0, fromFile.lastIndexOf("/"))
    : "";

  const parts = fromDir ? fromDir.split("/") : [];
  const importParts = importPath.split("/");

  for (const seg of importParts) {
    if (seg === ".") continue;
    if (seg === "..") {
      parts.pop();
    } else {
      parts.push(seg);
    }
  }

  return parts.join("/");
}

/** Check if a resolved path matches any file in the file set, trying common extensions. */
function fileExists(
  resolvedPath: string,
  fileSet: Set<string>
): boolean {
  // Direct match
  if (fileSet.has(resolvedPath)) return true;

  // Try common extensions
  const extensions = [".tsx", ".ts", ".jsx", ".js", ".css", ".json"];
  for (const ext of extensions) {
    if (fileSet.has(resolvedPath + ext)) return true;
  }

  // Try index files (directory import)
  for (const ext of extensions) {
    if (fileSet.has(resolvedPath + "/index" + ext)) return true;
  }

  return false;
}

/** Extract import paths from file content. Returns { path, isDefault } tuples. */
function extractImports(
  content: string
): Array<{ path: string; isDefault: boolean }> {
  const imports: Array<{ path: string; isDefault: boolean }> = [];

  // Match: import X from 'path', import { X } from 'path', import 'path'
  const importRegex =
    /import\s+(?:([\w$]+)\s*,?\s*)?(?:\{[^}]*\}\s*,?\s*)?(?:\*\s+as\s+\w+\s*,?\s*)?from\s+['"]([^'"]+)['"]/g;
  const sideEffectRegex = /import\s+['"]([^'"]+)['"]/g;

  let match: RegExpExecArray | null;

  while ((match = importRegex.exec(content)) !== null) {
    const defaultName = match[1]; // non-null means default import
    const path = match[2];
    imports.push({ path, isDefault: !!defaultName });
  }

  while ((match = sideEffectRegex.exec(content)) !== null) {
    imports.push({ path: match[1], isDefault: false });
  }

  return imports;
}

/** Check if file content has a default export. */
function hasDefaultExport(content: string): boolean {
  return (
    /export\s+default\s+/.test(content) ||
    /export\s*\{[^}]*\bdefault\b[^}]*\}/.test(content)
  );
}

/** Determine esbuild loader from file extension. */
function loaderForPath(
  filePath: string
): esbuildWasm.Loader {
  if (filePath.endsWith(".tsx")) return "tsx";
  if (filePath.endsWith(".ts")) return "ts";
  if (filePath.endsWith(".jsx")) return "jsx";
  if (filePath.endsWith(".js")) return "js";
  if (filePath.endsWith(".css")) return "css";
  if (filePath.endsWith(".json")) return "json";
  return "tsx"; // default for unknown
}

// ── Main Validation ─────────────────────────────────────────────────────────

/**
 * Run pre-flight validation on a set of file edits.
 *
 * @param edits - The generated file edits to validate
 * @param existingFiles - Map of existing project files (path → content) for import resolution
 * @returns PreflightResult with pass/fail and list of issues
 */
export async function runPreflightValidation(
  edits: FileEdit[],
  existingFiles: Map<string, string> = new Map()
): Promise<PreflightResult> {
  const issues: ValidationIssue[] = [];

  // Build a complete file set: new/modified files + existing files
  const fileSet = new Set<string>();
  const fileContents = new Map<string, string>();

  // Add existing files first
  for (const [path, content] of existingFiles) {
    fileSet.add(path);
    fileContents.set(path, content);
  }

  // Override with edits (new content takes precedence)
  for (const edit of edits) {
    if (edit.action === "delete") {
      fileSet.delete(edit.filePath);
      fileContents.delete(edit.filePath);
    } else if (edit.content != null) {
      fileSet.add(edit.filePath);
      fileContents.set(edit.filePath, edit.content);
    }
  }

  // Only validate files that are being created or modified
  const filesToValidate = edits.filter(
    (e) => e.action !== "delete" && e.content != null
  );

  // Initialize esbuild for syntax checking
  await ensureEsbuild();

  for (const edit of filesToValidate) {
    const { filePath, content } = edit;
    if (!content) continue;

    // Skip non-code files
    if (
      filePath.endsWith(".json") ||
      filePath.endsWith(".md") ||
      filePath.endsWith(".css") ||
      filePath === "index.html"
    ) {
      continue;
    }

    // 1. TypeScript syntax check via esbuild transform
    try {
      await esbuildWasm.transform(content, {
        loader: loaderForPath(filePath),
        jsx: "preserve",
        logLevel: "silent",
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      // Extract the useful part of the error message
      const cleanMsg = msg
        .replace(/^Transform failed with \d+ errors?:\s*/, "")
        .split("\n")[0];
      issues.push({
        filePath,
        type: "syntax",
        message: cleanMsg || msg,
      });
      // Skip further checks on files with syntax errors
      continue;
    }

    // 2. Check import resolution
    const imports = extractImports(content);
    for (const imp of imports) {
      // Only check relative imports (not bare specifiers like 'react', '@/...')
      if (!imp.path.startsWith(".")) continue;

      const resolved = resolveRelativeImport(filePath, imp.path);
      if (!fileExists(resolved, fileSet)) {
        issues.push({
          filePath,
          type: "import",
          message: `Import '${imp.path}' resolves to '${resolved}' which does not exist in the file set`,
        });
      }

      // 3. Check default export for default imports
      if (imp.isDefault) {
        const resolvedContent = findFileContent(resolved, fileContents);
        if (resolvedContent != null && !hasDefaultExport(resolvedContent)) {
          issues.push({
            filePath,
            type: "export",
            message: `Default import from '${imp.path}' but '${resolved}' has no default export`,
          });
        }
      }
    }
  }

  return {
    passed: issues.length === 0,
    issues,
  };
}

/** Find content for a resolved path, trying extensions. */
function findFileContent(
  resolvedPath: string,
  fileContents: Map<string, string>
): string | null {
  if (fileContents.has(resolvedPath)) return fileContents.get(resolvedPath)!;

  const extensions = [".tsx", ".ts", ".jsx", ".js"];
  for (const ext of extensions) {
    if (fileContents.has(resolvedPath + ext))
      return fileContents.get(resolvedPath + ext)!;
  }

  for (const ext of extensions) {
    const indexPath = resolvedPath + "/index" + ext;
    if (fileContents.has(indexPath)) return fileContents.get(indexPath)!;
  }

  return null;
}

/**
 * Auto-fix dangling imports by rewriting them to point at existing files.
 * This runs before AI regeneration — cheaper and more reliable than asking
 * the AI to fix import mismatches.
 *
 * Returns a new edits array with corrected imports, plus count of fixes.
 */
export function autoFixImports(edits: FileEdit[]): { edits: FileEdit[]; fixCount: number } {
  // Build a map of actual files by directory: "src/pages" → ["Landing", "Dashboard"]
  const filesByDir = new Map<string, string[]>();
  for (const edit of edits) {
    if (edit.action === "delete" || !edit.content) continue;
    const dir = edit.filePath.includes("/")
      ? edit.filePath.slice(0, edit.filePath.lastIndexOf("/"))
      : ".";
    const baseName = edit.filePath
      .replace(/^.*\//, "")
      .replace(/\.[tj]sx?$/, "");
    if (!filesByDir.has(dir)) filesByDir.set(dir, []);
    filesByDir.get(dir)!.push(baseName);
  }

  // Build full file set for existence checks
  const fileSet = new Set<string>();
  for (const edit of edits) {
    if (edit.action !== "delete" && edit.content != null) {
      fileSet.add(edit.filePath);
    }
  }

  let fixCount = 0;
  const COMMON_NAMES = new Set(["home", "index", "main", "landing", "landingpage", "homepage"]);

  const result = edits.map((edit) => {
    if (edit.action === "delete" || !edit.content) return edit;

    const importRe =
      /^([ \t]*import\s+.+?\s+from\s+['"])((?:\.\.?\/|@\/)[^'"]+)(['"].*$)/gm;

    let content = edit.content;
    let changed = false;

    content = content.replace(importRe, (match, prefix, importPath, suffix) => {
      // Resolve the import path
      let resolved: string;
      if (importPath.startsWith("@/")) {
        resolved = "src/" + importPath.slice(2);
      } else {
        const fromDir = edit.filePath.includes("/")
          ? edit.filePath.slice(0, edit.filePath.lastIndexOf("/"))
          : ".";
        resolved = resolveRelativeImport(edit.filePath, importPath);
      }

      // Check if it exists
      if (fileExists(resolved, fileSet)) return match;

      // Try to find the correct file in the same directory
      const resolvedDir = resolved.includes("/")
        ? resolved.slice(0, resolved.lastIndexOf("/"))
        : ".";
      const siblings = filesByDir.get(resolvedDir);
      if (!siblings || siblings.length === 0) return match;

      const importBase = resolved.replace(/^.*\//, "").toLowerCase();

      // Strategy 1: Exact single file in directory
      if (siblings.length === 1) {
        const corrected = importPath.replace(/\/[^/]+$/, "/" + siblings[0]);
        changed = true;
        fixCount++;
        return `${prefix}${corrected}${suffix}`;
      }

      // Strategy 2: Fuzzy match
      for (const sib of siblings) {
        const sibLower = sib.toLowerCase();
        if (sibLower.includes(importBase) || importBase.includes(sibLower)) {
          const corrected = importPath.replace(/\/[^/]+$/, "/" + sib);
          changed = true;
          fixCount++;
          return `${prefix}${corrected}${suffix}`;
        }
      }

      // Strategy 3: Common page name → first available
      if (COMMON_NAMES.has(importBase)) {
        const preferred = ["Landing", "LandingPage", "Home", "HomePage", "Main"];
        const pick = preferred.find((p) => siblings.includes(p)) || siblings[0];
        const corrected = importPath.replace(/\/[^/]+$/, "/" + pick);
        changed = true;
        fixCount++;
        return `${prefix}${corrected}${suffix}`;
      }

      return match; // No fix found
    });

    if (changed) return { ...edit, content };
    return edit;
  });

  return { edits: result, fixCount };
}

/**
 * Build a fix instruction for the AI to regenerate a specific file.
 */
export function buildFixInstruction(issues: ValidationIssue[]): string {
  const byFile = new Map<string, ValidationIssue[]>();
  for (const issue of issues) {
    const existing = byFile.get(issue.filePath) || [];
    existing.push(issue);
    byFile.set(issue.filePath, existing);
  }

  const parts: string[] = [
    "The following files have validation errors that must be fixed. Regenerate ONLY the failing files with corrections. Keep all other files exactly the same.\n",
  ];

  for (const [filePath, fileIssues] of byFile) {
    parts.push(`File: ${filePath}`);
    for (const issue of fileIssues) {
      parts.push(`  - [${issue.type}] ${issue.message}`);
    }
    parts.push("");
  }

  parts.push(
    "Fix these specific issues. Do not change any other files. Return the complete corrected file content."
  );

  return parts.join("\n");
}
